import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Send,
  ArrowLeft,
  Scale,
  Sparkles,
  MessageSquare,
  Loader2,
  Calculator,
  Menu,
  X,
  User,
  LogOut,
  Crown,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  IndianRupee,
  Calendar,
  FileText,
  Users,
  Building2,
  Clock,
  BadgeCheck,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import ThemeToggle from "../components/ThemeToggle";
import AuthModal from "../components/AuthModal";
import { API_BASE_URL, API_URL } from "../utils/api";

const API = API_URL;

// TaxSetu Logo URL
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_78cf9280-da7a-4b52-a25e-41321aa4bb13/artifacts/xl6xxrnp_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const LabourLawPage = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("taxsetu_labour_session") || null;
  });
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'calculator'
  
  // Compliance Wizard State
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    industry: '',
    state: '',
    totalEmployees: '',
    employeesBelow18k: '',
    employeesAbove18k: '',
    permanentCount: '',
    fteCount: '',
    gigCount: '',
    avgCtc: '',
    currentBasicPercent: ''
  });
  const [complianceScorecard, setComplianceScorecard] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Industry Options
  const industries = [
    { value: 'it_ites', label: 'IT / ITES', icon: '💻' },
    { value: 'manufacturing', label: 'Manufacturing', icon: '🏭' },
    { value: 'gig_aggregator', label: 'Gig / Aggregator', icon: '📱' },
    { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
    { value: 'construction', label: 'Construction', icon: '🏗️' },
    { value: 'retail', label: 'Retail / E-commerce', icon: '🛒' },
    { value: 'banking_finance', label: 'Banking & Finance', icon: '🏦' },
    { value: 'hospitality', label: 'Hospitality', icon: '🏨' },
    { value: 'other', label: 'Other', icon: '🏢' }
  ];

  // States & Union Territories (Alphabetical)
  const states = [
    'Andaman & Nicobar Islands',
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chandigarh',
    'Chhattisgarh',
    'Dadra & Nagar Haveli and Daman & Diu',
    'Delhi NCR',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jammu & Kashmir',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Ladakh',
    'Lakshadweep',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Puducherry',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Other'
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (activeTab === 'chat') {
      inputRef.current?.focus();
    }
  }, [activeTab]);

  // Quick queries for Labour Law
  const quickQueries = [
    { icon: <IndianRupee className="w-4 h-4" />, text: "50% Wage Rule kya hai?", query: "50% wage rule kya hai aur kaise calculate karein?" },
    { icon: <Clock className="w-4 h-4" />, text: "48-Hour F&F Rule", query: "Full and Final settlement 48 hours mein kaise karein?" },
    { icon: <Users className="w-4 h-4" />, text: "Fixed-Term Gratuity", query: "Fixed-term employees ko gratuity kab milegi?" },
    { icon: <AlertTriangle className="w-4 h-4" />, text: "Penalties", query: "Labour Code violations pe kya penalties hain?" },
    { icon: <Building2 className="w-4 h-4" />, text: "ESIC/PF Changes", query: "New Labour Codes mein ESI aur PF rules kya badal gaye hain?" },
    { icon: <Calendar className="w-4 h-4" />, text: "Implementation Timeline", query: "Labour Codes kab se effective hue aur compliance deadline kya hai?" }
  ];

  const sendMessage = async (message = inputValue) => {
    if (!message.trim() || isLoading) return;

    const userMessage = { role: "user", content: message.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setLoadingStatus('📖 Reading your query...');

    // Progressive loading messages
    const loadingMessages = [
      { delay: 1500, text: '📚 Searching Labour Codes 2026...' },
      { delay: 3500, text: '🔍 Checking compliance requirements...' },
      { delay: 6000, text: '⚖️ Analyzing provisions...' },
      { delay: 9000, text: '✍️ Preparing your answer...' },
    ];

    const statusTimers = loadingMessages.map(({ delay, text }) => 
      setTimeout(() => setLoadingStatus(text), delay)
    );

    // Add empty assistant message
    setMessages(prev => [...prev, { role: "assistant", content: "" }]);

    try {
      const response = await fetch(`${API}/labour-law/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          session_id: sessionId,
          user_email: user?.email || ''
        })
      });

      if (!response.ok) throw new Error('Request failed');
      
      const data = await response.json();
      
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem("taxsetu_labour_session", data.session_id);
      }
      
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: "assistant",
          content: data.response
        };
        return newMessages;
      });

    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response. Please try again.");
      setMessages(prev => prev.slice(0, -2));
    } finally {
      statusTimers.forEach(timer => clearTimeout(timer));
      setIsLoading(false);
      setLoadingStatus('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Generate Compliance Roadmap
  const generateComplianceRoadmap = () => {
    setIsGenerating(true);
    
    const data = wizardData;
    const totalEmp = parseInt(data.totalEmployees) || 0;
    const below18k = parseInt(data.employeesBelow18k) || 0;
    const above18k = parseInt(data.employeesAbove18k) || 0;
    const permanent = parseInt(data.permanentCount) || 0;
    const fte = parseInt(data.fteCount) || 0;
    const gig = parseInt(data.gigCount) || 0;
    const avgCtc = parseFloat(data.avgCtc) || 0;
    const basicPercent = parseFloat(data.currentBasicPercent) || 30;

    // Generate compliance requirements
    const roadmap = {
      wagesCode: { requirements: [], salaryImpact: null },
      socialSecurity: { requirements: [] },
      irCode: { requirements: [] },
      oshCode: { requirements: [] },
      checklist: []
    };

    // CODE ON WAGES - Mandatory Requirements
    roadmap.wagesCode.requirements.push({
      title: '50% Basic Wage Rule',
      mandatory: true,
      description: 'Basic Pay + Dearness Allowance must constitute at least 50% of total CTC',
      currentStatus: basicPercent >= 50 ? 'Compliant' : 'Action Required',
      details: basicPercent < 50 ? `Current Basic: ${basicPercent}% | Required: ≥50%` : `Current Basic: ${basicPercent}% ✓`
    });
    
    roadmap.wagesCode.requirements.push({
      title: 'Salary Payment Timeline',
      mandatory: true,
      description: 'Wages must be paid by the 7th of the following month',
      currentStatus: 'Verify',
      details: 'Monthly wages: By 7th | Weekly wages: Within 2 days | Daily wages: Same day'
    });

    roadmap.wagesCode.requirements.push({
      title: 'Overtime Compensation',
      mandatory: true,
      description: 'Overtime wages at 2× the normal rate',
      currentStatus: 'Verify',
      details: 'Maximum 8 hours/day, 48 hours/week. Overtime beyond this at double rate.'
    });

    // Calculate salary restructuring impact - scenario based
    if (basicPercent < 50) {
      roadmap.wagesCode.salaryImpact = {
        currentPercent: basicPercent,
        requiredPercent: 50,
        shortfall: 50 - basicPercent,
        scenarioText: `If your current Basic is ${basicPercent}% of CTC, you need to increase it to 50%. This will increase PF contribution (12% of higher Basic) and Gratuity liability. Example: For a ₹10 Lakh CTC employee, Basic increases from ₹${Math.round(10 * basicPercent / 12).toLocaleString()}k to ₹${Math.round(10 * 50 / 12).toLocaleString()}k per month.`
      };
      
      roadmap.checklist.push('☐ Restructure salary to ensure Basic + DA ≥ 50% of CTC');
      roadmap.checklist.push('☐ Update payroll software with new salary structure');
      roadmap.checklist.push('☐ Communicate salary restructuring to employees');
    }

    roadmap.checklist.push('☐ Ensure salary disbursement by 7th of each month');

    // SOCIAL SECURITY CODE - Mandatory Requirements
    // PF
    if (totalEmp >= 20) {
      roadmap.socialSecurity.requirements.push({
        title: 'Provident Fund (PF)',
        mandatory: true,
        description: 'PF registration and contribution mandatory for establishments with 20+ employees',
        currentStatus: 'Mandatory',
        details: 'Employer: 12% of wages | Employee: 12% of wages | Wage ceiling: ₹15,000/month for employer contribution'
      });
      roadmap.checklist.push('☐ Verify PF registration (EPFO)');
      roadmap.checklist.push('☐ Ensure timely PF deposits (by 15th of following month)');
    }

    // ESI
    if (totalEmp >= 10 && below18k > 0) {
      roadmap.socialSecurity.requirements.push({
        title: 'Employees\' State Insurance (ESI)',
        mandatory: true,
        description: `ESI mandatory for ${below18k} employees earning ≤ ₹21,000/month`,
        currentStatus: 'Mandatory',
        details: 'Employer: 3.25% of wages | Employee: 0.75% of wages | Covers medical, disability, maternity benefits'
      });
      roadmap.checklist.push('☐ Register with ESIC');
      roadmap.checklist.push('☐ Ensure ESI contributions for eligible employees');
    }

    // FTE Gratuity
    if (fte > 0) {
      roadmap.socialSecurity.requirements.push({
        title: 'Fixed-Term Employee Gratuity',
        mandatory: true,
        description: `${fte} Fixed-Term Employees eligible for pro-rata gratuity after 1 year of service`,
        currentStatus: 'Mandatory',
        details: 'Formula: (Last Drawn Wages × 15 days × Years of Service) ÷ 26 | Maximum: ₹20 Lakhs'
      });
      roadmap.checklist.push('☐ Update FTE contracts with gratuity clause');
      roadmap.checklist.push('☐ Provision for FTE gratuity liability');
    }

    // Regular Gratuity
    roadmap.socialSecurity.requirements.push({
      title: 'Gratuity (Regular Employees)',
      mandatory: totalEmp >= 10,
      description: 'Gratuity payable to employees with 5+ years of service (or on death/disability)',
      currentStatus: totalEmp >= 10 ? 'Mandatory' : 'Not Applicable',
      details: 'Payment within 30 days of becoming due, else 10% simple interest applies'
    });

    // Gig Workers
    if (gig > 0 || data.industry === 'gig_aggregator') {
      roadmap.socialSecurity.requirements.push({
        title: 'Gig Worker Social Security Fund',
        mandatory: true,
        description: 'Platform/Aggregator companies must contribute 1-2% of annual turnover to Social Security Fund',
        currentStatus: 'Mandatory',
        details: 'Registration with Social Security Organisation required. Covers life & disability insurance, health benefits.'
      });
      roadmap.checklist.push('☐ Register with Social Security Organisation');
      roadmap.checklist.push('☐ Budget 1-2% of turnover for Gig Worker Social Security Fund');
    }

    roadmap.checklist.push('☐ Complete 48-hour Full & Final settlement process automation');

    // INDUSTRIAL RELATIONS CODE - Requirements
    if (totalEmp >= 300) {
      roadmap.irCode.requirements.push({
        title: 'Standing Orders',
        mandatory: true,
        description: 'Draft and certify Standing Orders for industrial establishments with 300+ workers',
        currentStatus: 'Mandatory',
        details: 'Must be certified within 6 months of applicability. Covers: working hours, leave, termination, misconduct procedures.'
      });
      roadmap.checklist.push('☐ Draft Standing Orders as per Schedule I');
      roadmap.checklist.push('☐ Submit to Certifying Officer for certification');
    }

    if (totalEmp >= 20) {
      roadmap.irCode.requirements.push({
        title: 'Grievance Redressal Committee',
        mandatory: totalEmp >= 300,
        description: 'Internal committee to address worker grievances',
        currentStatus: totalEmp >= 300 ? 'Mandatory' : 'Recommended',
        details: 'Equal representation from employer and workers. Grievances to be resolved within 30 days.'
      });
      roadmap.checklist.push('☐ Constitute Grievance Redressal Committee');
      roadmap.checklist.push('☐ Define grievance resolution process');
    }

    if (totalEmp >= 300) {
      roadmap.irCode.requirements.push({
        title: 'Retrenchment Approval',
        mandatory: true,
        description: 'Government approval required for layoffs, retrenchment, or closure',
        currentStatus: 'Mandatory',
        details: '60 days notice to government required. 15 days wages for each year of service as compensation.'
      });
    }

    roadmap.irCode.requirements.push({
      title: 'Trade Union Recognition',
      mandatory: false,
      description: 'Negotiating Union/Council to be recognized if workers request',
      currentStatus: 'As Applicable',
      details: 'Sole Negotiating Union if 51%+ workers are members. Negotiating Council if no union has 51%.'
    });

    // OSH CODE - Requirements
    const isHighRisk = ['manufacturing', 'construction', 'healthcare'].includes(data.industry);
    
    roadmap.oshCode.requirements.push({
      title: 'Working Hours & Leave',
      mandatory: true,
      description: 'Maximum 8 hours/day, 48 hours/week with mandatory weekly off',
      currentStatus: 'Mandatory',
      details: 'Spread over maximum 12 hours including breaks. Minimum 1 day off per week. Annual leave: 1 day per 20 days worked.'
    });

    if (isHighRisk || totalEmp >= 100) {
      roadmap.oshCode.requirements.push({
        title: 'Safety Committee',
        mandatory: isHighRisk,
        description: 'Occupational Safety and Health Committee for hazardous industries',
        currentStatus: isHighRisk ? 'Mandatory' : 'Recommended',
        details: 'Committee to conduct safety audits, accident investigations, and recommend safety measures.'
      });
      roadmap.checklist.push('☐ Form Safety Committee with worker representatives');
    }

    roadmap.oshCode.requirements.push({
      title: 'Annual Health Check-up',
      mandatory: isHighRisk,
      description: 'Free annual health check-up for workers aged 40 years and above',
      currentStatus: isHighRisk ? 'Mandatory' : 'Recommended',
      details: 'Employer to bear the cost. Records to be maintained for 5 years.'
    });
    if (isHighRisk) {
      roadmap.checklist.push('☐ Arrange annual health check-ups for workers 40+');
    }

    if (data.industry === 'construction') {
      roadmap.oshCode.requirements.push({
        title: 'Building Workers Welfare Fund',
        mandatory: true,
        description: 'Registration with Building and Other Construction Workers Welfare Board',
        currentStatus: 'Mandatory',
        details: 'Cess: 1-2% of construction cost. Provides welfare benefits to construction workers.'
      });
      roadmap.checklist.push('☐ Register with Building Workers Welfare Board');
    }

    if (totalEmp >= 50) {
      roadmap.oshCode.requirements.push({
        title: 'Creche Facility',
        mandatory: true,
        description: 'Creche facility required if 50 or more workers are employed',
        currentStatus: 'Verify',
        details: 'For children under 6 years of women employees. Within 500 meters of establishment.'
      });
      roadmap.checklist.push('☐ Assess creche requirement based on women employee count');
    }

    roadmap.oshCode.requirements.push({
      title: 'Appointment Letter',
      mandatory: true,
      description: 'Written appointment letter mandatory for all employees',
      currentStatus: 'Mandatory',
      details: 'Must include: designation, wages, period of employment, other terms and conditions.'
    });
    roadmap.checklist.push('☐ Issue appointment letters to all employees');

    setTimeout(() => {
      setComplianceScorecard({
        roadmap,
        profile: {
          industry: industries.find(i => i.value === data.industry)?.label || data.industry,
          state: data.state,
          totalEmployees: totalEmp,
          employmentMix: { permanent, fte, gig },
          avgCtc,
          basicPercent
        },
        deadline: '1st April 2026',
        generatedAt: new Date().toISOString()
      });
      setIsGenerating(false);
      setWizardStep(5);
    }, 1500);
  };

  const resetWizard = () => {
    setWizardStep(1);
    setWizardData({
      industry: '',
      state: '',
      totalEmployees: '',
      employeesBelow18k: '',
      employeesAbove18k: '',
      permanentCount: '',
      fteCount: '',
      gigCount: '',
      avgCtc: '',
      currentBasicPercent: ''
    });
    setComplianceScorecard(null);
  };

  const formatMessage = (content) => {
    let result = content;
    
    // Bold text
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
    // Italic text
    result = result.replace(/\*(.*?)\*/g, '<em class="text-slate-200 italic">$1</em>');
    // Headings - using cyan for professional look
    result = result.replace(/^### (.*?)$/gm, '<h3 class="text-cyan-400 font-semibold mt-3 mb-1 border-l-2 border-cyan-500 pl-2">$1</h3>');
    result = result.replace(/^## (.*?)$/gm, '<h2 class="text-white font-bold text-lg mt-4 mb-2">$1</h2>');
    // Bullet points
    result = result.replace(/^- (.*?)$/gm, '<li class="ml-4 text-slate-200">$1</li>');
    result = result.replace(/^• (.*?)$/gm, '<li class="ml-4 text-slate-200">$1</li>');
    // Numbered lists
    result = result.replace(/^(\d+)\. (.*?)$/gm, '<li class="ml-4 text-slate-200"><span class="text-cyan-400 font-bold">$1.</span> $2</li>');
    // Line breaks
    result = result.replace(/\n\n/g, '<br/><br/>');
    result = result.replace(/\n/g, '<br/>');
    // Readiness Tip highlight - emerald/green for tips
    result = result.replace(/Readiness Tip:(.*?)(?=<br|$)/gi, '<div class="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg"><span class="text-emerald-400 font-semibold">💡 Readiness Tip:</span><span class="text-emerald-200">$1</span></div>');
    
    return result;
  };

  return (
    <div className="h-screen bg-slate-950 flex flex-col overflow-hidden">
      {/* Header with Integrated Tabs */}
      <header className="glass border-b border-slate-800 sticky top-0 z-30 shrink-0">
        <div className="w-full px-4 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2">
              <img 
                src={TAXSETU_LOGO}
                alt="TaxSetu Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="font-heading font-semibold text-white text-base sm:text-lg">
                Labour Law Expert
              </h1>
            </div>

            {/* Tabs in Header */}
            <div className="hidden sm:flex items-center gap-2 ml-6 border-l border-slate-700 pl-6">
              <button
                onClick={() => setActiveTab('chat')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === 'chat'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
                data-testid="tab-chat"
              >
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Ask Expert
              </button>
              <button
                onClick={() => setActiveTab('calculator')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  activeTab === 'calculator'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
                data-testid="tab-calculator"
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Compliance Wizard
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Mobile Tabs */}
            <div className="flex sm:hidden items-center gap-1">
              <button
                onClick={() => setActiveTab('chat')}
                className={`p-2 rounded-lg transition-all ${
                  activeTab === 'chat'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-slate-400'
                }`}
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              <button
                onClick={() => setActiveTab('calculator')}
                className={`p-2 rounded-lg transition-all ${
                  activeTab === 'calculator'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-slate-400'
                }`}
              >
                <FileText className="w-5 h-5" />
              </button>
            </div>

            <ThemeToggle />
            
            {isAuthenticated ? (
              <button className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 transition-colors">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <User className="w-5 h-5 text-slate-400" />
                )}
                <span className="text-sm text-white hidden md:inline">{user?.name?.split(' ')[0]}</span>
              </button>
            ) : (
              <Button 
                variant="default"
                size="sm"
                onClick={() => setShowAuthModal(true)}
                className="btn-primary text-white text-xs px-3"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'chat' ? (
          /* Chat Interface */
          <div className="h-full flex flex-col">
            {messages.length === 0 ? (
              /* Empty State - Centered with optical alignment */
              <div className="flex-1 flex flex-col items-center justify-start px-4 pt-[12vh] sm:pt-[15vh] md:pt-[18vh]">
                <motion.div 
                  className="w-full max-w-2xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="text-center mb-10">
                    {/* TaxSetu Logo */}
                    <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6">
                      <img 
                        src={TAXSETU_LOGO} 
                        alt="TaxSetu AI" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-heading font-semibold text-white mb-3">
                      Labour Codes 2026 Expert
                    </h2>
                    <p className="text-slate-400 mb-4">
                      Your guide to the 4 New Labour Codes
                    </p>
                    
                    {/* Implementation Note */}
                    <p className="text-slate-500 text-sm italic">
                      Note: Notified on 21st Nov 2025; full implementation expected from 1st April 2026.
                    </p>
                  </div>
                  
                  {/* Centered Input Box - Same width as main chat */}
                  <div className="w-full">
                    <div className="flex items-center gap-3 bg-slate-900/80 border border-slate-700 rounded-2xl px-5 py-4 focus-within:border-cyan-500/50 transition-all shadow-xl">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about 50% wage rule, F&F settlement, gratuity..."
                        className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none py-1 text-base"
                        disabled={isLoading}
                        data-testid="labour-chat-input"
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={!inputValue.trim() || isLoading}
                        className="btn-primary text-white rounded-xl px-5 py-2.5 h-auto"
                        data-testid="labour-send-btn"
                      >
                        <Send className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    {/* Quick Queries with breathing room */}
                    <div className="flex flex-wrap justify-center gap-3 mt-6">
                      {quickQueries.slice(0, 4).map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => sendMessage(item.query)}
                          className="px-4 py-2 bg-slate-800/40 hover:bg-slate-700/60 border border-slate-700/50 hover:border-cyan-500/40 rounded-full text-sm text-slate-400 hover:text-cyan-400 transition-all"
                          data-testid={`quick-query-${idx}`}
                        >
                          {item.text}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            ) : (
              /* Messages View */
              <>
                <ScrollArea className="flex-1">
                  <div className="w-full px-4 lg:px-8 py-6">
                    <div className="space-y-4 max-w-3xl mx-auto">
                      <AnimatePresence>
                        {messages.map((msg, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[85%] lg:max-w-[75%]`}>
                              <div 
                                className={`rounded-2xl px-4 py-3 ${
                                  msg.role === 'user' 
                                    ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 text-white rounded-br-sm' 
                                    : 'bg-slate-800/80 border border-slate-700 rounded-bl-sm'
                                }`}
                              >
                                {msg.role === 'assistant' ? (
                                  msg.content ? (
                                    <div 
                                      className="text-slate-200 text-sm leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                    />
                                  ) : (
                                    <div className="flex flex-col gap-2">
                                      <div className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                                        <span className="text-cyan-400 text-sm font-medium">{loadingStatus || 'Starting...'}</span>
                                      </div>
                                    </div>
                                  )
                                ) : (
                                  <p className="text-white text-sm">{msg.content}</p>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </ScrollArea>
                
                {/* Input Area */}
                <div className="glass border-t border-slate-800 p-4">
                  <div className="w-full max-w-4xl mx-auto">
                    <div className="flex items-center gap-3 bg-slate-900/80 border border-slate-700 rounded-2xl px-4 py-2 focus-within:border-cyan-500/50 transition-all">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask a follow-up question..."
                        className="flex-1 bg-transparent text-white placeholder-slate-500 focus:outline-none py-2"
                        disabled={isLoading}
                      />
                      <Button
                        onClick={() => sendMessage()}
                        disabled={!inputValue.trim() || isLoading}
                        className="btn-primary text-white rounded-xl px-4 py-2 h-auto"
                      >
                        <Send className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          /* Compliance Readiness Wizard */
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-4 py-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Wizard Progress */}
                {wizardStep < 5 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-400">Step {wizardStep} of 4</span>
                      <span className="text-sm text-cyan-400">{Math.round(wizardStep * 25)}% Complete</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-500"
                        style={{ width: `${wizardStep * 25}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Step 1: Industry Selection */}
                {wizardStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-cyan-500/10 rounded-2xl border border-cyan-500/30">
                        <Building2 className="w-8 h-8 text-cyan-400" />
                      </div>
                      <h2 className="text-2xl font-heading font-semibold text-white mb-3">
                        Labour Codes Compliance Wizard
                      </h2>
                      <p className="text-slate-400 max-w-lg mx-auto">
                        Discover what's mandatory for your industry — from PF/ESI to maternity leave, 
                        overtime rules, safety norms, and more. Get your complete compliance roadmap for April 1st, 2026.
                      </p>
                    </div>

                    <div>
                      <label className="text-base text-slate-300 mb-4 block font-medium">Select Your Industry</label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {industries.map((ind) => (
                          <button
                            key={ind.value}
                            onClick={() => setWizardData({...wizardData, industry: ind.value})}
                            className={`p-5 rounded-xl border text-center transition-all ${
                              wizardData.industry === ind.value
                                ? 'bg-cyan-500/20 border-cyan-500/50 text-white'
                                : 'bg-slate-800/40 border-slate-700 text-slate-400 hover:border-slate-600 hover:text-white'
                            }`}
                            data-testid={`industry-${ind.value}`}
                          >
                            <span className="text-3xl block mb-3">{ind.icon}</span>
                            <span className="text-sm font-medium">{ind.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button
                        onClick={() => wizardData.industry && setWizardStep(2)}
                        disabled={!wizardData.industry}
                        className="btn-primary text-white px-8"
                      >
                        Continue
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Geography */}
                {wizardStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                        Select Your State
                      </h2>
                      <p className="text-slate-400">
                        State-specific rules may apply to your organization
                      </p>
                    </div>

                    <div>
                      <label className="text-base text-slate-300 mb-3 block font-medium">Operating State / Union Territory</label>
                      <select
                        value={wizardData.state}
                        onChange={(e) => setWizardData({...wizardData, state: e.target.value})}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer text-base"
                      >
                        <option value="" className="bg-slate-900">-- Select State / UT --</option>
                        {states.map((state) => (
                          <option key={state} value={state} className="bg-slate-900 py-2">
                            {state}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-slate-500 mt-2">All 28 States & 8 Union Territories included</p>
                    </div>

                    <div className="flex justify-between pt-4">
                      <Button
                        onClick={() => setWizardStep(1)}
                        variant="outline"
                        className="border-slate-600 text-slate-300"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <Button
                        onClick={() => wizardData.state && setWizardStep(3)}
                        disabled={!wizardData.state}
                        className="btn-primary text-white px-8"
                      >
                        Continue
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Employee Profiling */}
                {wizardStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                        Employee Profile
                      </h2>
                      <p className="text-slate-400">
                        Help us understand your workforce composition
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-slate-300 mb-2 block font-medium">Total Employee Strength</label>
                        <input
                          type="number"
                          value={wizardData.totalEmployees}
                          onChange={(e) => setWizardData({...wizardData, totalEmployees: e.target.value})}
                          placeholder="e.g., 250"
                          className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
                          <label className="text-sm text-white mb-1 block font-medium">
                            Employees earning ≤ ₹18,000/month
                          </label>
                          <p className="text-xs text-slate-500 mb-3">Classified as "Workers" under new codes. ESI benefits applicable.</p>
                          <input
                            type="number"
                            value={wizardData.employeesBelow18k}
                            onChange={(e) => setWizardData({...wizardData, employeesBelow18k: e.target.value})}
                            placeholder="e.g., 100"
                            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                          />
                        </div>
                        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
                          <label className="text-sm text-white mb-1 block font-medium">
                            Employees earning &gt; ₹18,000/month
                          </label>
                          <p className="text-xs text-slate-500 mb-3">Typically supervisory/managerial roles. Different compliance rules apply.</p>
                          <input
                            type="number"
                            value={wizardData.employeesAbove18k}
                            onChange={(e) => setWizardData({...wizardData, employeesAbove18k: e.target.value})}
                            placeholder="e.g., 150"
                            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                          />
                        </div>
                      </div>

                      <div className="pt-2">
                        <label className="text-base text-slate-300 mb-4 block font-medium">Employment Type Breakdown</label>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 flex flex-col">
                            <label className="text-sm text-white mb-1 block font-medium">Permanent</label>
                            <p className="text-xs text-slate-500 mb-3 flex-1">Full-time employees with no fixed end date. Eligible for gratuity after 5 years.</p>
                            <input
                              type="number"
                              value={wizardData.permanentCount}
                              onChange={(e) => setWizardData({...wizardData, permanentCount: e.target.value})}
                              placeholder="e.g., 180"
                              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                            />
                          </div>
                          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 flex flex-col">
                            <label className="text-sm text-white mb-1 block font-medium">Fixed-Term (FTE)</label>
                            <p className="text-xs text-slate-500 mb-3 flex-1">Contract employees with defined end date. Now eligible for gratuity after just 1 year.</p>
                            <input
                              type="number"
                              value={wizardData.fteCount}
                              onChange={(e) => setWizardData({...wizardData, fteCount: e.target.value})}
                              placeholder="e.g., 50"
                              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                            />
                          </div>
                          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 flex flex-col">
                            <label className="text-sm text-white mb-1 block font-medium">Gig / Contractual</label>
                            <p className="text-xs text-slate-500 mb-3 flex-1">Freelancers, consultants, delivery partners. Platform must contribute to Social Security Fund.</p>
                            <input
                              type="number"
                              value={wizardData.gigCount}
                              onChange={(e) => setWizardData({...wizardData, gigCount: e.target.value})}
                              placeholder="e.g., 20"
                              className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <Button
                        onClick={() => setWizardStep(2)}
                        variant="outline"
                        className="border-slate-600 text-slate-300"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <Button
                        onClick={() => wizardData.totalEmployees && setWizardStep(4)}
                        disabled={!wizardData.totalEmployees}
                        className="btn-primary text-white px-8"
                      >
                        Continue
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4: Salary Structure */}
                {wizardStep === 4 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                        Salary Structure Check
                      </h2>
                      <p className="text-slate-400">
                        Quick check for 50% Wage Rule compliance
                      </p>
                    </div>

                    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
                      <div className="mb-6">
                        <h3 className="text-white font-medium mb-2">What is the 50% Wage Rule?</h3>
                        <p className="text-slate-400 text-sm">
                          Under the new Code on Wages, <span className="text-cyan-400">Basic Pay + DA must be at least 50% of total CTC</span>. 
                          Most companies currently keep Basic at 20-40%, which will need restructuring.
                        </p>
                      </div>

                      <div>
                        <label className="text-sm text-slate-300 mb-3 block font-medium">
                          What is your current Basic + DA as % of CTC?
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            min="15"
                            max="60"
                            value={wizardData.currentBasicPercent || 30}
                            onChange={(e) => setWizardData({...wizardData, currentBasicPercent: e.target.value})}
                            className="flex-1 h-3 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                          />
                          <span className={`text-2xl font-bold min-w-[60px] text-right ${
                            parseFloat(wizardData.currentBasicPercent || 30) >= 50 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {wizardData.currentBasicPercent || 30}%
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>15%</span>
                          <span className="text-amber-400 font-medium">50% (Required minimum)</span>
                          <span>60%</span>
                        </div>
                        
                        {parseFloat(wizardData.currentBasicPercent || 30) < 50 && (
                          <p className="text-amber-400 text-sm mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            ⚠️ Your Basic is below 50%. Salary restructuring will be required, which may impact PF and Gratuity costs.
                          </p>
                        )}
                        {parseFloat(wizardData.currentBasicPercent || 30) >= 50 && (
                          <p className="text-emerald-400 text-sm mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                            ✓ Your salary structure is compliant with the 50% wage rule.
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-slate-500 text-center">
                      Don't know your exact %? Most IT/ITES companies are at 25-35%. Manufacturing typically at 35-45%.
                    </p>

                    <div className="flex justify-between pt-4">
                      <Button
                        onClick={() => setWizardStep(3)}
                        variant="outline"
                        className="border-slate-600 text-slate-300"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <Button
                        onClick={generateComplianceRoadmap}
                        disabled={isGenerating}
                        className="btn-primary text-white px-8"
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            Generate Compliance Roadmap
                            <Sparkles className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 5: Compliance Requirement Dashboard */}
                {wizardStep === 5 && complianceScorecard && (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                        Mandatory Compliance Roadmap
                      </h2>
                      <p className="text-slate-400">
                        {complianceScorecard.profile.industry} • {complianceScorecard.profile.state} • {complianceScorecard.profile.totalEmployees} Employees
                      </p>
                      <p className="text-cyan-400 text-sm mt-2">
                        Implementation Deadline: <span className="font-semibold">1st April 2026</span>
                      </p>
                    </div>

                    {/* Code on Wages */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-3">
                        <h3 className="text-amber-400 font-semibold flex items-center gap-2">
                          <IndianRupee className="w-5 h-5" />
                          Code on Wages, 2019
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {complianceScorecard.roadmap.wagesCode.requirements.map((req, i) => (
                          <div key={i} className="border-b border-slate-700/50 pb-4 last:border-0 last:pb-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-medium">{req.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                req.mandatory 
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                  : 'bg-slate-600/30 text-slate-400 border border-slate-600'
                              }`}>
                                {req.mandatory ? 'MANDATORY' : 'RECOMMENDED'}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{req.description}</p>
                            <p className="text-slate-500 text-xs">{req.details}</p>
                            {req.currentStatus && (
                              <p className={`text-xs mt-2 ${
                                req.currentStatus === 'Compliant' ? 'text-emerald-400' : 
                                req.currentStatus === 'Action Required' ? 'text-red-400' : 'text-amber-400'
                              }`}>
                                Status: {req.currentStatus}
                              </p>
                            )}
                          </div>
                        ))}
                        
                        {/* Salary Impact Analysis - Scenario Based */}
                        {complianceScorecard.roadmap.wagesCode.salaryImpact && (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
                            <h4 className="text-amber-400 font-medium mb-3">What This Means For You</h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-red-400 font-bold text-lg">{complianceScorecard.roadmap.wagesCode.salaryImpact.currentPercent}%</span>
                                  <span className="text-slate-500">→</span>
                                  <span className="text-emerald-400 font-bold text-lg">{complianceScorecard.roadmap.wagesCode.salaryImpact.requiredPercent}%</span>
                                </div>
                                <span className="text-slate-400">Basic increase required</span>
                              </div>
                              <p className="text-slate-300">{complianceScorecard.roadmap.wagesCode.salaryImpact.scenarioText}</p>
                              <div className="pt-2 border-t border-amber-500/20">
                                <p className="text-amber-300 text-xs">
                                  <span className="font-medium">Impact:</span> Higher PF contribution (employer + employee), increased Gratuity liability, lower take-home pay for employees unless CTC is revised upward.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Social Security Code */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="bg-cyan-500/10 border-b border-cyan-500/30 px-4 py-3">
                        <h3 className="text-cyan-400 font-semibold flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          Code on Social Security, 2020
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {complianceScorecard.roadmap.socialSecurity.requirements.map((req, i) => (
                          <div key={i} className="border-b border-slate-700/50 pb-4 last:border-0 last:pb-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-medium">{req.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                req.mandatory 
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                  : req.currentStatus === 'Not Applicable' 
                                    ? 'bg-slate-600/30 text-slate-500 border border-slate-600'
                                    : 'bg-slate-600/30 text-slate-400 border border-slate-600'
                              }`}>
                                {req.mandatory ? 'MANDATORY' : req.currentStatus === 'Not Applicable' ? 'N/A' : 'AS APPLICABLE'}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{req.description}</p>
                            <p className="text-slate-500 text-xs">{req.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Industrial Relations Code */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="bg-purple-500/10 border-b border-purple-500/30 px-4 py-3">
                        <h3 className="text-purple-400 font-semibold flex items-center gap-2">
                          <Scale className="w-5 h-5" />
                          Industrial Relations Code, 2020
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {complianceScorecard.roadmap.irCode.requirements.map((req, i) => (
                          <div key={i} className="border-b border-slate-700/50 pb-4 last:border-0 last:pb-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-medium">{req.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                req.mandatory 
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                  : 'bg-slate-600/30 text-slate-400 border border-slate-600'
                              }`}>
                                {req.mandatory ? 'MANDATORY' : req.currentStatus}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{req.description}</p>
                            <p className="text-slate-500 text-xs">{req.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* OSH Code */}
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
                      <div className="bg-emerald-500/10 border-b border-emerald-500/30 px-4 py-3">
                        <h3 className="text-emerald-400 font-semibold flex items-center gap-2">
                          <BadgeCheck className="w-5 h-5" />
                          Occupational Safety, Health & Working Conditions Code, 2020
                        </h3>
                      </div>
                      <div className="p-4 space-y-4">
                        {complianceScorecard.roadmap.oshCode.requirements.map((req, i) => (
                          <div key={i} className="border-b border-slate-700/50 pb-4 last:border-0 last:pb-0">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-medium">{req.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                req.mandatory 
                                  ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                                  : 'bg-slate-600/30 text-slate-400 border border-slate-600'
                              }`}>
                                {req.mandatory ? 'MANDATORY' : req.currentStatus}
                              </span>
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{req.description}</p>
                            <p className="text-slate-500 text-xs">{req.details}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Implementation Checklist */}
                    <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <FileText className="w-5 h-5 text-cyan-400" />
                          Implementation Checklist
                        </h3>
                        <Button
                          onClick={() => {
                            const checklistText = `LABOUR CODES 2026 - IMPLEMENTATION CHECKLIST\n` +
                              `Generated: ${new Date().toLocaleDateString()}\n` +
                              `Organization: ${complianceScorecard.profile.industry} | ${complianceScorecard.profile.state}\n` +
                              `Employees: ${complianceScorecard.profile.totalEmployees}\n` +
                              `Deadline: 1st April 2026\n\n` +
                              complianceScorecard.roadmap.checklist.join('\n');
                            
                            const blob = new Blob([checklistText], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'Labour_Codes_2026_Checklist.txt';
                            a.click();
                            URL.revokeObjectURL(url);
                            toast.success('Checklist downloaded successfully');
                          }}
                          size="sm"
                          className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs"
                        >
                          Download Checklist
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {complianceScorecard.roadmap.checklist.map((item, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm py-2 border-b border-slate-700/30 last:border-0">
                            <div className="w-5 h-5 rounded border border-slate-600 shrink-0 mt-0.5" />
                            <span className="text-slate-300">{item.replace('☐ ', '')}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Readiness Tip */}
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                      <p className="text-emerald-400 text-sm">
                        <span className="font-semibold">💡 Readiness Tip:</span>{' '}
                        Start with salary restructuring (50% wage rule) as it impacts PF, ESI, and Gratuity calculations. 
                        Ensure your payroll software is updated and communicate changes transparently to employees.
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between pt-4">
                      <Button
                        onClick={resetWizard}
                        variant="outline"
                        className="border-slate-600 text-slate-300"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Start Over
                      </Button>
                      <Button
                        onClick={() => setActiveTab('chat')}
                        className="btn-primary text-white"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Ask Expert
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        trigger="default"
      />
    </div>
  );
};

export default LabourLawPage;
